import { useState } from 'react';
import { Paperclip, Upload } from 'lucide-react';
import { useExpenseDocuments } from '../../hooks/useExpenseDocuments';
import { useEntityAttachments } from '../../hooks/useEntityAttachments';
import { EntityModal } from '../common/EntityModal';
import { ExpenseDocumentsSection } from './ExpenseDocumentsSection';
import { EntityAttachmentSection } from '../attachments/EntityAttachmentSection';

interface ExpenseAttachmentButtonProps {
  expenseItemId: string;
  size?: number;
}

export function ExpenseAttachmentButton({
  expenseItemId,
  size = 14,
}: ExpenseAttachmentButtonProps) {
  const { data: docs = [] } = useExpenseDocuments(expenseItemId);
  const { data: attachments = [] } = useEntityAttachments('EXPENSE_ITEM', expenseItemId);
  const [open, setOpen] = useState(false);

  const count = docs.length + attachments.length;
  const tooltip = count > 0 ? `${count} attachment${count !== 1 ? 's' : ''}` : 'Add attachment';

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={tooltip}
        className="relative p-1 text-gray-400 hover:text-primary-600 transition-colors"
      >
        {count > 0 ? (
          <>
            <Paperclip size={size} className="text-primary-600" />
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold bg-primary-600 text-white rounded-full flex items-center justify-center">
              {count}
            </span>
          </>
        ) : (
          <Upload size={size} />
        )}
      </button>

      <EntityModal
        open={open}
        onClose={() => setOpen(false)}
        title="Documents & Attachments"
        size="md"
      >
        <div className="space-y-6">
          {docs.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Imported Documents
              </h4>
              <ExpenseDocumentsSection expenseItemId={expenseItemId} />
            </div>
          )}
          <div>
            {docs.length > 0 && (
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Attachments
              </h4>
            )}
            <EntityAttachmentSection entityType="EXPENSE_ITEM" entityId={expenseItemId} />
          </div>
        </div>
      </EntityModal>
    </>
  );
}
